import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import { Check, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { SUITE_NAME } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [programName, setProgramName] = useState('');
  
  const sessionId = searchParams.get('session_id');
  const programId = searchParams.get('program_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/myinsul/checkout-status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Try to get program name
        try {
          const programRes = await axios.get(
            `${API_URL}/api/myinsul/programs/${programId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setProgramName(programRes.data.name);
        } catch (e) {
          console.error('Failed to fetch program name:', e);
        }
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-6">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin mb-4" />
            <h1 className="text-2xl font-bold mb-2">Verifying Payment...</h1>
            <p className="text-muted-foreground">Please wait while we confirm your subscription.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-emerald-700">Subscription Activated!</h1>
            <p className="text-muted-foreground mb-6">
              {programName 
                ? `You're now enrolled in "${programName}". Week 1 content is ready for you!`
                : "Your subscription is now active. Week 1 content is ready for you!"
              }
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"
              >
                Go to Dashboard
              </Button>
              <Button 
                onClick={() => navigate('/myinsul')}
                variant="outline"
                className="w-full rounded-full"
              >
                Browse More Programs
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-red-700">Payment Issue</h1>
            <p className="text-muted-foreground mb-6">
              We couldn't verify your payment. If you were charged, please contact support.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/myinsul')}
                className="w-full rounded-full"
              >
                Return to {SUITE_NAME}
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccessPage;
